import type { GraphQLFieldResolver, GraphQLSchema } from 'graphql';
import { defaultFieldResolver, parse } from 'graphql';
import { execute } from '@graphql-tools/executor';

/**
 * Execute a GraphQL operation against the schema
 *
 * @param schema The GraphQL schema
 * @param operationString The GraphQL operation as a string
 * @param variableValues Variables for the operation
 * @param schemaVersion Optional version identifier of the schema
 * @returns The execution result
 */
export const executeOperation = async (
  schema: GraphQLSchema,
  operationString: string,
  variableValues: Record<string, any> = {}
) => {
  try {
    const document = parse(operationString);

    const { fieldResolver, missingResolverFields } =
      createTrackingMissingFieldsFieldResolver();

    const result = await execute({
      schema,
      document,
      variableValues,
      fieldResolver,
    });

    if (missingResolverFields.size > 0) {
      const extensions = {
        ...(result as any).extensions,
        uxpLink: {
          ...(result as any).extensions?.uxpLink,
          missingResolvers: Array.from(missingResolverFields),
        },
      };
      return { ...(result as any), extensions };
    }

    return result as any;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error executing GraphQL operation:', error);
    return {
      data: null,
      errors: [{ message: (error as Error).message }],
    };
  }
};

/**
 * Creates a field resolver that tracks fields that do not have explicit resolvers and resolve to null/undefined
 *
 * @returns A field resolver and a set of missing resolver fields
 */
const createTrackingMissingFieldsFieldResolver = (): {
  fieldResolver: GraphQLFieldResolver<unknown, unknown>;
  missingResolverFields: Set<string>;
} => {
  const missingResolverFields = new Set<string>();
  const fieldResolver = (
    source: any,
    args: any,
    context: any,
    info: any
  ): any => {
    const maybeValue = defaultFieldResolver(source, args, context, info);

    const recordIfMissing = (val: unknown) => {
      if (val === null || typeof val === 'undefined') {
        missingResolverFields.add(`${info.parentType.name}.${info.fieldName}`);
      }
      return val as unknown;
    };

    if (maybeValue && typeof (maybeValue as any).then === 'function') {
      return (maybeValue as Promise<unknown>).then(recordIfMissing);
    }
    return recordIfMissing(maybeValue);
  };
  return { fieldResolver, missingResolverFields };
};
